import { darken, lighten, Theme } from '@mui/material/styles';
import { orange, purple } from '@mui/material/colors';
import { ChartColorScheme } from '../../../../../types/Enums';
import { reduceArray } from './Chart.utils';

export const ChartColorSchemeLabels: Record<ChartColorScheme, string> = {
    [ChartColorScheme.Primary]: 'Default',
    [ChartColorScheme.Red]: 'Red',
    [ChartColorScheme.Blue]: 'Blue',
    [ChartColorScheme.Green]: 'Green',
    [ChartColorScheme.Purple]: 'Purple',
    [ChartColorScheme.Orange]: 'Orange',
};

export const ChartColorSchemeOptions: Array<{ value: ChartColorScheme; label: string }> = [
    { value: ChartColorScheme.Primary, label: ChartColorSchemeLabels[ChartColorScheme.Primary] },
    { value: ChartColorScheme.Red, label: ChartColorSchemeLabels[ChartColorScheme.Red] },
    { value: ChartColorScheme.Blue, label: ChartColorSchemeLabels[ChartColorScheme.Blue] },
    { value: ChartColorScheme.Green, label: ChartColorSchemeLabels[ChartColorScheme.Green] },
    { value: ChartColorScheme.Purple, label: ChartColorSchemeLabels[ChartColorScheme.Purple] },
    { value: ChartColorScheme.Orange, label: ChartColorSchemeLabels[ChartColorScheme.Orange] },
];

export function parseChartColorScheme(value: string | undefined | null): ChartColorScheme | undefined {
    if (!value) {
        return undefined;
    }
    const validValues = Object.values(ChartColorScheme) as string[];
    if (validValues.includes(value)) {
        return value as ChartColorScheme;
    }
    return undefined;
}

export function isHexChartColor(value: unknown): value is string {
    return typeof value === 'string' && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(value);
}

function normalizeHex(hex: string): string {
    const h = hex.replace('#', '').trim();
    if (h.length === 3) {
        return (
            '#' +
            h
                .split('')
                .map((c) => c + c)
                .join('')
                .toLowerCase()
        );
    }
    return '#' + h.toLowerCase();
}

export function parseColorSchemeField(raw: string | undefined | null): ChartColorScheme | string | undefined {
    if (raw == null || !String(raw).trim()) {
        return undefined;
    }
    const t = String(raw).trim();
    if (isHexChartColor(t)) {
        return normalizeHex(t);
    }
    return parseChartColorScheme(t);
}

const colorSchemeToThemePaletteKey: Record<ChartColorScheme, keyof Theme['palette'] | 'purple' | 'orange'> = {
    [ChartColorScheme.Primary]: 'primary',
    [ChartColorScheme.Red]: 'error',
    [ChartColorScheme.Blue]: 'info',
    [ChartColorScheme.Green]: 'success',
    [ChartColorScheme.Purple]: 'purple',
    [ChartColorScheme.Orange]: 'orange',
};

// WIP for the new brand world: pin each color scheme to a base hex pulled from the new-brand
// dashboard ordinal aesthetic, then derive shades via ladderFromBase. Refine these per design
// guidance as the new theme stabilizes.
const NEW_BRAND_SCHEME_BASES: Partial<Record<ChartColorScheme, string>> = {
    [ChartColorScheme.Red]: '#FF4F79',
    [ChartColorScheme.Blue]: '#14AFF1',
    [ChartColorScheme.Green]: '#8FD163',
    [ChartColorScheme.Purple]: '#C17EE4',
    [ChartColorScheme.Orange]: '#FC7500',
};

const NEW_BRAND_SHADE_COUNT = 6;

// First entry is the base hex itself; subsequent entries step ~10% darker each,
// up to ~50% darker at the last slot. Tweak the multiplier as the new theme matures.
function darkeningRampFromBase(base: string, count: number): string[] {
    return Array.from({ length: count }, (_, i) => darken(base, (i / (count - 1)) * 0.5));
}

const colorShades = [900, 800, 700, 600, 500, 400, 300, 200, 100, 50] as const;

function ladderFromBase(base: string): string[] {
    const steps = colorShades.length;
    return Array.from({ length: steps }, (_, i) => {
        const t = i / (steps - 1);
        return t <= 0.5 ? darken(base, (1 - t * 2) * 0.28) : lighten(base, (t * 2 - 1) * 0.32);
    });
}

function expandDashboardChartOrdinal(ordinal: readonly string[]): string[] {
    return ordinal.flatMap((base) => ladderFromBase(base));
}

/** Brand ordinal palette: use design hexes in order, wrapping when there are more series than colors. */
function pickOrdinalFlat(ordinal: readonly string[], n: number): string[] {
    return Array.from({ length: n }, (_, i) => ordinal[i % ordinal.length]);
}

export function getChartColors(colorScheme: ChartColorScheme | string | undefined, theme: Theme): string[] {
    if (isHexChartColor(colorScheme)) {
        return ladderFromBase(normalizeHex(colorScheme));
    }

    const scheme = (colorScheme as ChartColorScheme | undefined) ?? ChartColorScheme.Primary;

    const ordinal = theme.palette.dashboardChartOrdinal;
    const isNewBrand = (ordinal?.length ?? 0) > 0;
    if (scheme === ChartColorScheme.Primary && isNewBrand) {
        return expandDashboardChartOrdinal(ordinal!);
    }

    // WIP new-brand path for non-Primary schemes: start at the base hex and step progressively
    // darker. Six shades total — refine the count/curve per design guidance.
    if (isNewBrand) {
        const newBrandBase = NEW_BRAND_SCHEME_BASES[scheme];
        if (newBrandBase) {
            return darkeningRampFromBase(newBrandBase, NEW_BRAND_SHADE_COUNT);
        }
    }

    const paletteKey = colorSchemeToThemePaletteKey[scheme];

    if (paletteKey === 'purple') {
        return colorShades.map((shade) => purple[shade]);
    }
    if (paletteKey === 'orange') {
        return colorShades.map((shade) => orange[shade]);
    }

    const palette = theme.palette[paletteKey];

    if (!palette || typeof palette !== 'object') {
        return getChartColors(ChartColorScheme.Primary, theme);
    }

    const paletteRecord = palette as unknown as Record<string, unknown>;
    const colors: string[] = [];

    for (const shade of colorShades) {
        const color = paletteRecord[shade];
        if (typeof color === 'string') {
            colors.push(color);
        }
    }

    if (colors.length === 0 && typeof paletteRecord.main === 'string') {
        colors.push(paletteRecord.main);
    }

    return colors;
}

export function resolveChartPaletteForDataCount(
    colorScheme: ChartColorScheme | string | undefined,
    theme: Theme,
    dataCount: number
): string[] {
    if (isHexChartColor(colorScheme)) {
        return reduceArray(getChartColors(colorScheme, theme), dataCount);
    }
    const enumScheme = (colorScheme as ChartColorScheme | undefined) ?? ChartColorScheme.Primary;
    const full = getChartColors(enumScheme, theme);
    const ordinal = theme.palette.dashboardChartOrdinal;
    const isNewBrand = (ordinal?.length ?? 0) > 0;
    const ordinalMode = enumScheme === ChartColorScheme.Primary && isNewBrand;
    if (ordinalMode) {
        return pickOrdinalFlat(ordinal!, dataCount);
    }
    // New-brand non-Primary: 10 shades cycled when there are more slices than colors,
    // so adjacent extras don't all collapse to the lightest shade.
    if (isNewBrand && NEW_BRAND_SCHEME_BASES[enumScheme]) {
        return pickOrdinalFlat(full, dataCount);
    }
    return reduceArray(full, dataCount);
}

export function getChartSwatchColor(colorScheme: ChartColorScheme | string | undefined, theme: Theme): string {
    if (isHexChartColor(colorScheme)) {
        return normalizeHex(colorScheme);
    }
    const ordinal = theme.palette.dashboardChartOrdinal;
    if (colorScheme === ChartColorScheme.Primary && ordinal?.length) {
        return ordinal[0];
    }
    const colors = getChartColors(colorScheme, theme);
    return colors[4] ?? colors[0] ?? theme.palette.primary.main;
}
